// src/pages/portal/PortalReservationsPage.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PortalLayout from './PortalLayout';
import { reservationService, waitingListService, guestService, occupationService, diningOptionService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import VisualTableSelector from '../../components/pos/VisualTableSelector';
import useIsMobile from '../../hooks/useIsMobile';
import MobileCard from '../../components/common/MobileCard';
import MobileHero from '../../components/common/MobileHero';
import { C } from '../../styles/theme';
import dedupeByName from '../../utils/dedupeByName';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Status config ────────────────────────────────────────────────────────────
const RES_STATUS = {
    booked:    { color: C.blue,      bg: C.blueLight,  label: 'booked' },
    seated:    { color: C.green,     bg: C.greenLight,  label: 'seated' },
    completed: { color: C.muted,     bg: '#f1f5f9',     label: 'completed' },
    cancelled: { color: C.red,       bg: C.redLight,    label: 'cancelled' },
    no_show:   { color: C.amber,     bg: C.amberLight,  label: 'no_show' },
};

const WL_STATUS = {
    waiting:  { color: C.blue,  bg: C.blueLight,  label: 'waiting' },
    notified: { color: C.amber, bg: C.amberLight,  label: 'notified' },
    seated:   { color: C.green, bg: C.greenLight,  label: 'seated' },
    left:     { color: C.muted, bg: '#f1f5f9',     label: 'left' },
    no_show:  { color: C.amber, bg: C.amberLight,  label: 'no_show' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateISO = (d) => {
    const date = new Date(d);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const formatTime = (timeStr) => {
    if (!timeStr) return '';
    // handle HH:MM:SS or HH:MM
    const parts = timeStr.split(':');
    const h = parseInt(parts[0], 10);
    const m = parts[1] || '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m} ${ampm}`;
};

const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
};

// Map reservation/waitlist status → MobileCard tone
const RES_TONE = { booked: 'gray', seated: 'green', completed: 'gray', cancelled: 'red', no_show: 'amber' };
const WL_TONE = { waiting: 'gray', notified: 'amber', seated: 'green', left: 'gray', no_show: 'amber' };

const timeAgo = (dateStr, t) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('just_now');
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ${mins % 60}m`;
    return `${Math.floor(hrs / 24)}d`;
};

// ─── Shared components ────────────────────────────────────────────────────────
const Badge = ({ children, color, bg }) => (
    <span style={{
        fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
        borderRadius: 5, padding: '2px 8px',
        background: bg, color, display: 'inline-block',
        fontFamily: FONT, textTransform: 'capitalize',
    }}>
        {children}
    </span>
);

const StatusBadge = ({ status, map, t }) => {
    const cfg = map[status] || { color: C.muted, bg: '#f1f5f9', label: status };
    return <Badge color={cfg.color} bg={cfg.bg}>{t(cfg.label)}</Badge>;
};

const ToastContainer = ({ toasts }) => (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
        {toasts.map(toast => (
            <div key={toast.id} style={{
                padding: '10px 16px', borderRadius: 9, fontSize: 13, fontWeight: 600,
                background: toast.type === 'success' ? '#eff5ff' : C.redLight,
                border: `1.5px solid ${toast.type === 'success' ? '#86efac' : '#fca5a5'}`,
                color: toast.type === 'success' ? '#16a34a' : C.red,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                fontFamily: FONT, pointerEvents: 'auto', minWidth: 220,
            }}>
                {toast.message}
            </div>
        ))}
    </div>
);

const Modal = ({ open, onClose, title, children, width = 520 }) => {
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 12, width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: FONT }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{title}</div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: C.mutedLight, cursor: 'pointer', lineHeight: 1, padding: 0 }} aria-label="Close">{'✕'}</button>
                </div>
                <div style={{ padding: 20 }}>{children}</div>
            </div>
        </div>
    );
};

const InfoTooltip = ({ text }) => {
    const [show, setShow] = useState(false);
    return (
        <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 6 }}>
            <span
                onMouseEnter={() => setShow(true)}
                onMouseLeave={() => setShow(false)}
                style={{ cursor: 'help', color: C.mutedLight, display: 'flex', alignItems: 'center' }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                </svg>
            </span>
            {show && (
                <div style={{
                    position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                    background: C.text, color: C.white, fontSize: 11, fontWeight: 500, padding: '6px 10px',
                    borderRadius: 6, zIndex: 100, marginBottom: 4,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontFamily: FONT, maxWidth: 280,
                    whiteSpace: 'normal', lineHeight: 1.4,
                }}>
                    {text}
                </div>
            )}
        </span>
    );
};

const EmptyState = ({ icon, title, subtitle, cta, onCta }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
        <div style={{ width: 56, height: 56, background: C.greenLight, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            {icon}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: cta ? 16 : 0 }}>{subtitle}</div>
        {cta && (
            <button onClick={onCta} style={{
                padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: C.green, color: C.white, fontSize: 13, fontWeight: 600, fontFamily: FONT,
            }}>
                {cta}
            </button>
        )}
    </div>
);

// ─── Guest search component ──────────────────────────────────────────────────
const GuestSearch = ({ value, onChange, t }) => {
    const [search, setSearch] = useState('');
    const [results, setResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [newGuest, setNewGuest] = useState({ name: '', phone: '', email: '' });
    const [creating, setCreating] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setShowResults(false); };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    useEffect(() => {
        if (!search || search.length < 2) { setResults([]); return; }
        const timer = setTimeout(async () => {
            try {
                const data = await guestService.list(search);
                setResults(Array.isArray(data) ? data : []);
                setShowResults(true);
            } catch { setResults([]); }
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const handleCreate = async () => {
        if (!newGuest.name.trim()) return;
        setCreating(true);
        try {
            const guest = await guestService.create(newGuest);
            onChange(guest);
            setShowCreate(false);
            setSearch(guest.name);
            setNewGuest({ name: '', phone: '', email: '' });
        } catch { /* toast handled by parent */ }
        setCreating(false);
    };

    const inputStyle = {
        width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.border}`,
        fontSize: 13, fontFamily: FONT, outline: 'none', boxSizing: 'border-box',
    };

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4, display: 'block' }}>{t('guest')}</label>
            {value ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: C.greenLight, borderRadius: 8, border: `1.5px solid ${C.greenBorder}` }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: C.text, flex: 1 }}>{value.name}</span>
                    {value.phone && <span style={{ fontSize: 11, color: C.muted }}>{value.phone}</span>}
                    <button onClick={() => { onChange(null); setSearch(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: 14, lineHeight: 1, padding: 0 }}>{'✕'}</button>
                </div>
            ) : (
                <>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onFocus={() => results.length > 0 && setShowResults(true)}
                        placeholder={t('search_guest')}
                        style={inputStyle}
                    />
                    {showResults && results.length > 0 && (
                        <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                            background: C.white, border: `1px solid ${C.border}`, borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: 200, overflowY: 'auto',
                        }}>
                            {results.map(g => (
                                <div key={g.id} onClick={() => { onChange(g); setSearch(g.name); setShowResults(false); }}
                                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${C.border}` }}
                                    onMouseEnter={e => e.currentTarget.style.background = C.greenLight}
                                    onMouseLeave={e => e.currentTarget.style.background = C.white}
                                >
                                    <span style={{ fontWeight: 600, color: C.text }}>{g.name}</span>
                                    {g.phone && <span style={{ fontSize: 11, color: C.muted }}>{g.phone}</span>}
                                </div>
                            ))}
                        </div>
                    )}
                    {showResults && search.length >= 2 && results.length === 0 && (
                        <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                            background: C.white, border: `1px solid ${C.border}`, borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, padding: '10px 12px',
                            fontSize: 13, color: C.muted, textAlign: 'center',
                        }}>
                            {t('no_guests_found')}
                        </div>
                    )}
                    <button onClick={() => setShowCreate(true)} style={{
                        marginTop: 6, background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 12, fontWeight: 600, color: C.green, fontFamily: FONT, padding: 0,
                    }}>
                        + {t('create_new_guest')}
                    </button>
                    {showCreate && (
                        <div style={{ marginTop: 8, padding: 12, background: '#f8fafc', borderRadius: 8, border: `1px solid ${C.border}` }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <input placeholder={t('guest_name')} value={newGuest.name} onChange={e => setNewGuest(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
                                <input placeholder={t('phone')} value={newGuest.phone} onChange={e => setNewGuest(p => ({ ...p, phone: e.target.value }))} style={inputStyle} />
                                <input placeholder={t('email')} value={newGuest.email} onChange={e => setNewGuest(p => ({ ...p, email: e.target.value }))} style={inputStyle} />
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button onClick={handleCreate} disabled={creating || !newGuest.name.trim()} style={{
                                        padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                        background: C.green, color: C.white, fontSize: 12, fontWeight: 600, fontFamily: FONT,
                                        opacity: creating || !newGuest.name.trim() ? 0.6 : 1,
                                    }}>
                                        {creating ? t('creating') : t('create')}
                                    </button>
                                    <button onClick={() => setShowCreate(false)} style={{
                                        padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.border}`, cursor: 'pointer',
                                        background: C.white, color: C.muted, fontSize: 12, fontWeight: 600, fontFamily: FONT,
                                    }}>
                                        {t('cancel')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};


// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const PortalReservationsPage = () => {
    const { tenantConfig } = useAuth();
    const { t } = useLanguage();
    const isMobile = useIsMobile();

    // ─── State ────────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState('reservations');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [viewMode, setViewMode] = useState('list');
    const [reservations, setReservations] = useState([]);
    const [waitlistEntries, setWaitlistEntries] = useState([]);
    const [diningOptions, setDiningOptions] = useState([]);
    const [activeOccupations, setActiveOccupations] = useState([]);
    const [selectedDiningOptionId, setSelectedDiningOptionId] = useState(null);
    const [filterDiningOptionId, setFilterDiningOptionId] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [showNewReservationModal, setShowNewReservationModal] = useState(false);
    const [showAddWaitlistModal, setShowAddWaitlistModal] = useState(false);
    const [showFloorPlan, setShowFloorPlan] = useState(false);

    const openFloorPlan = useCallback(() => {
        const withLayout = diningOptions.find(opt => opt.tables_config && typeof opt.tables_config === 'object');
        if (withLayout && !selectedDiningOptionId) setSelectedDiningOptionId(withLayout.id);
        else if (withLayout) {
            const current = diningOptions.find(o => o.id === selectedDiningOptionId);
            if (!current?.tables_config || typeof current.tables_config !== 'object') setSelectedDiningOptionId(withLayout.id);
        }
        setShowFloorPlan(true);
    }, [diningOptions, selectedDiningOptionId]);
    const [seatMode, setSeatMode] = useState(null); // { type: 'reservation'|'waitlist', id, partySize }
    const [loading, setLoading] = useState(true);
    const [toasts, setToasts] = useState([]);
    const [confirmAction, setConfirmAction] = useState(null); // { type, id, label }

    // ─── Toast helper ─────────────────────────────────────────────────────────
    const toast = useCallback((message, type = 'success') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
    }, []);

    // ─── Floor plan modal Escape key ────────────────────────────────────────
    useEffect(() => {
        if (!showFloorPlan) return;
        const handler = (e) => {
            if (e.key === 'Escape') {
                setSeatMode(null);
                setShowFloorPlan(false);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [showFloorPlan]);

    // ─── Data fetching ────────────────────────────────────────────────────────
    const fetchDiningOptions = useCallback(async () => {
        try {
            const data = dedupeByName(await diningOptionService.getAll());
            setDiningOptions(Array.isArray(data) ? data : []);
            if (!selectedDiningOptionId && Array.isArray(data) && data.length > 0) {
                setSelectedDiningOptionId(data[0].id);
            }
        } catch (err) {
            console.error('Failed to fetch dining options:', err);
        }
    }, [selectedDiningOptionId]);

    const fetchReservations = useCallback(async () => {
        try {
            const dateStr = formatDateISO(selectedDate);
            const params = { date: dateStr };
            if (filterDiningOptionId) params.dining_option_id = filterDiningOptionId;
            if (filterStatus) params.status = filterStatus;
            const data = await reservationService.list(params);
            setReservations(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch reservations:', err);
        }
    }, [selectedDate, filterDiningOptionId, filterStatus]);

    const fetchWaitlist = useCallback(async () => {
        try {
            const params = {};
            if (filterDiningOptionId) params.dining_option_id = filterDiningOptionId;
            const data = await waitingListService.list(params);
            setWaitlistEntries(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch waiting list:', err);
        }
    }, [filterDiningOptionId]);

    const fetchOccupations = useCallback(async () => {
        try {
            const data = await occupationService.listActive(selectedDiningOptionId);
            setActiveOccupations(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch occupations:', err);
        }
    }, [selectedDiningOptionId]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await fetchDiningOptions();
            setLoading(false);
        };
        init();
    }, []);

    useEffect(() => {
        if (activeTab === 'reservations') fetchReservations();
        else fetchWaitlist();
    }, [activeTab, fetchReservations, fetchWaitlist]);

    useEffect(() => { fetchOccupations(); }, [fetchOccupations]);

    // ─── Date navigation ──────────────────────────────────────────────────────
    const shiftDate = useCallback((days) => {
        setSelectedDate(prev => {
            const d = new Date(prev);
            d.setDate(d.getDate() + days);
            return d;
        });
    }, []);

    // ─── Actions ──────────────────────────────────────────────────────────────
    const handleSeatReservation = useCallback((res) => {
        setSeatMode({ type: 'reservation', id: res.id, partySize: res.party_size });
        openFloorPlan();
    }, []);

    const handleSeatWaitlistEntry = useCallback((entry) => {
        setSeatMode({ type: 'waitlist', id: entry.id, partySize: entry.party_size });
        openFloorPlan();
    }, []);

    const handleTableSelect = useCallback(async (tableNumber) => {
        if (!seatMode) return;
        try {
            if (seatMode.type === 'reservation') {
                await reservationService.seat(seatMode.id, { table_number: tableNumber, dining_option_id: selectedDiningOptionId });
                toast(t('reservation_seated'));
                fetchReservations();
            } else {
                await waitingListService.seat(seatMode.id, { table_number: tableNumber, dining_option_id: selectedDiningOptionId });
                toast(t('guest_seated'));
                fetchWaitlist();
            }
            fetchOccupations();
            setSeatMode(null);
            setShowFloorPlan(false);
        } catch (err) {
            toast(t('action_failed'), 'error');
        }
    }, [seatMode, selectedDiningOptionId, fetchReservations, fetchWaitlist, fetchOccupations, t, toast]);

    const handleCancelReservation = useCallback(async (id) => {
        try {
            await reservationService.cancel(id);
            toast(t('reservation_cancelled'));
            fetchReservations();
        } catch { toast(t('action_failed'), 'error'); }
        setConfirmAction(null);
    }, [fetchReservations, t, toast]);

    const handleNoShowReservation = useCallback(async (id) => {
        try {
            await reservationService.noShow(id);
            toast(t('marked_no_show'));
            fetchReservations();
        } catch { toast(t('action_failed'), 'error'); }
        setConfirmAction(null);
    }, [fetchReservations, t, toast]);

    const handleNotifyWaitlist = useCallback(async (id) => {
        try {
            await waitingListService.notify(id);
            toast(t('guest_notified'));
            fetchWaitlist();
        } catch { toast(t('action_failed'), 'error'); }
    }, [fetchWaitlist, t, toast]);

    const handleLeaveWaitlist = useCallback(async (id) => {
        try {
            await waitingListService.leave(id);
            toast(t('guest_left'));
            fetchWaitlist();
        } catch { toast(t('action_failed'), 'error'); }
        setConfirmAction(null);
    }, [fetchWaitlist, t, toast]);

    const handleNoShowWaitlist = useCallback(async (id) => {
        try {
            await waitingListService.noShow(id);
            toast(t('marked_no_show'));
            fetchWaitlist();
        } catch { toast(t('action_failed'), 'error'); }
        setConfirmAction(null);
    }, [fetchWaitlist, t, toast]);

    // ─── Confirm dialog ───────────────────────────────────────────────────────
    const executeConfirmAction = useCallback(() => {
        if (!confirmAction) return;
        const { type, id } = confirmAction;
        if (type === 'cancel_reservation') handleCancelReservation(id);
        else if (type === 'noshow_reservation') handleNoShowReservation(id);
        else if (type === 'leave_waitlist') handleLeaveWaitlist(id);
        else if (type === 'noshow_waitlist') handleNoShowWaitlist(id);
    }, [confirmAction, handleCancelReservation, handleNoShowReservation, handleLeaveWaitlist, handleNoShowWaitlist]);

    // ─── Build diningOptions map for VisualTableSelector ──────────────────────
    const diningOptionsMap = useMemo(() => {
        const map = {};
        diningOptions.forEach(opt => {
            if (opt.tables_config) {
                map[opt.name] = opt.tables_config;
            }
        });
        return map;
    }, [diningOptions]);

    const selectedDiningOptionName = useMemo(() => {
        const opt = diningOptions.find(o => o.id === selectedDiningOptionId);
        return opt ? opt.name : '';
    }, [diningOptions, selectedDiningOptionId]);

    // ─── Calendar view hours ──────────────────────────────────────────────────
    const calendarHours = useMemo(() => {
        const hours = [];
        for (let h = 8; h <= 23; h++) hours.push(h);
        return hours;
    }, []);

    // ─── Action button ────────────────────────────────────────────────────────
    const ActionBtn = ({ label, onClick, color = C.green, bg = C.greenLight, small = false }) => (
        <button onClick={onClick} style={{
            padding: small ? '3px 8px' : '4px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
            background: bg, color, fontSize: 11, fontWeight: 600, fontFamily: FONT, whiteSpace: 'nowrap',
        }}>
            {label}
        </button>
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // NEW RESERVATION MODAL
    // ═══════════════════════════════════════════════════════════════════════════
    const NewReservationModal = () => {
        const [guest, setGuest] = useState(null);
        const [date, setDate] = useState(formatDateISO(selectedDate));
        const [time, setTime] = useState('');
        const [partySize, setPartySize] = useState(2);
        const [diningOptionId, setDiningOptionId] = useState(selectedDiningOptionId || '');
        const [tableNumber, setTableNumber] = useState('');
        const [durationEstimate, setDurationEstimate] = useState('');
        const [notes, setNotes] = useState('');
        const [saving, setSaving] = useState(false);

        const handleSave = async () => {
            if (!guest || !date || !time) { toast(t('fill_required_fields'), 'error'); return; }
            setSaving(true);
            try {
                const scheduled_for = new Date(`${date}T${time}`).toISOString();
                const payload = {
                    guest_id: guest.id,
                    scheduled_for,
                    party_size: partySize,
                    dining_option_id: parseInt(diningOptionId) || diningOptions[0]?.id,
                    table_number: tableNumber || undefined,
                    duration_estimate: durationEstimate ? parseInt(durationEstimate, 10) : undefined,
                    notes: notes || undefined,
                };
                await reservationService.create(payload);
                toast(t('reservation_created'));
                setShowNewReservationModal(false);
                fetchReservations();
            } catch (err) {
                const d = err?.response?.data?.detail;
                toast(typeof d === 'string' ? d : Array.isArray(d) ? d.map(e => e.msg).join(', ') : t('action_failed'), 'error');
            }
            setSaving(false);
        };

        const inputStyle = {
            width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.border}`,
            fontSize: 13, fontFamily: FONT, outline: 'none', boxSizing: 'border-box',
        };

        return (
            <Modal open={showNewReservationModal} onClose={() => setShowNewReservationModal(false)} title={t('new_reservation')} width={480}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <GuestSearch value={guest} onChange={setGuest} t={t} />

                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4, display: 'block' }}>{t('date')}</label>
                            <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4, display: 'block' }}>{t('time')}</label>
                            <input type="time" value={time} onChange={e => setTime(e.target.value)} style={inputStyle} />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4, display: 'block' }}>{t('party_size')}</label>
                            <input type="number" min="1" max="50" value={partySize} onChange={e => setPartySize(parseInt(e.target.value, 10) || 1)} style={inputStyle} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4, display: 'block' }}>{t('dining_option')}</label>
                            <select value={diningOptionId} onChange={e => setDiningOptionId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                                <option value="">{t('select')}</option>
                                {diningOptions.filter(opt => opt.tables_config && typeof opt.tables_config === 'object').map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
                            </select>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4, display: 'block' }}>{t('table')} ({t('optional')})</label>
                            <input type="text" value={tableNumber} onChange={e => setTableNumber(e.target.value)} placeholder={t('table_number')} style={inputStyle} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4, display: 'block' }}>{t('duration_minutes')} ({t('optional')})</label>
                            <input type="number" min="15" step="15" value={durationEstimate} onChange={e => setDurationEstimate(e.target.value)} placeholder="60" style={inputStyle} />
                        </div>
                    </div>

                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4, display: 'block' }}>{t('notes')} ({t('optional')})</label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder={t('reservation_notes_placeholder')} style={{ ...inputStyle, resize: 'vertical' }} />
                    </div>

                    <button onClick={handleSave} disabled={saving} style={{
                        padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        background: C.green, color: C.white, fontSize: 14, fontWeight: 700, fontFamily: FONT,
                        opacity: saving ? 0.6 : 1, width: '100%',
                    }}>
                        {saving ? t('saving') : t('create_reservation')}
                    </button>
                </div>
            </Modal>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // ADD TO WAITLIST MODAL
    // ═══════════════════════════════════════════════════════════════════════════
    const AddWaitlistModal = () => {
        const [guest, setGuest] = useState(null);
        const [partySize, setPartySize] = useState(2);
        const [diningOptionId, setDiningOptionId] = useState(selectedDiningOptionId || '');
        const [expectedWait, setExpectedWait] = useState('');
        const [saving, setSaving] = useState(false);

        const handleSave = async () => {
            if (!guest) { toast(t('fill_required_fields'), 'error'); return; }
            setSaving(true);
            try {
                await waitingListService.add({
                    guest_id: guest.id,
                    party_size: partySize,
                    dining_option_id: diningOptionId || undefined,
                    expected_wait_minutes: expectedWait ? parseInt(expectedWait, 10) : undefined,
                });
                toast(t('added_to_waitlist'));
                setShowAddWaitlistModal(false);
                fetchWaitlist();
            } catch (err) {
                const d = err?.response?.data?.detail;
                toast(typeof d === 'string' ? d : Array.isArray(d) ? d.map(e => e.msg).join(', ') : t('action_failed'), 'error');
            }
            setSaving(false);
        };

        const inputStyle = {
            width: '100%', padding: '8px 12px', borderRadius: 8, border: `1.5px solid ${C.border}`,
            fontSize: 13, fontFamily: FONT, outline: 'none', boxSizing: 'border-box',
        };

        return (
            <Modal open={showAddWaitlistModal} onClose={() => setShowAddWaitlistModal(false)} title={t('add_to_waitlist')} width={420}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <GuestSearch value={guest} onChange={setGuest} t={t} />

                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4, display: 'block' }}>{t('party_size')}</label>
                            <input type="number" min="1" max="50" value={partySize} onChange={e => setPartySize(parseInt(e.target.value, 10) || 1)} style={inputStyle} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4, display: 'block' }}>{t('dining_option')}</label>
                            <select value={diningOptionId} onChange={e => setDiningOptionId(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                                <option value="">{t('select')}</option>
                                {diningOptions.filter(opt => opt.tables_config && typeof opt.tables_config === 'object').map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 4, display: 'block' }}>{t('expected_wait')} ({t('optional')})</label>
                        <input type="number" min="5" step="5" value={expectedWait} onChange={e => setExpectedWait(e.target.value)} placeholder={t('minutes')} style={inputStyle} />
                    </div>

                    <button onClick={handleSave} disabled={saving} style={{
                        padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        background: C.green, color: C.white, fontSize: 14, fontWeight: 700, fontFamily: FONT,
                        opacity: saving ? 0.6 : 1, width: '100%',
                    }}>
                        {saving ? t('saving') : t('add_to_waitlist')}
                    </button>
                </div>
            </Modal>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // CONFIRM ACTION MODAL
    // ═══════════════════════════════════════════════════════════════════════════
    const ConfirmModal = () => {
        if (!confirmAction) return null;
        return (
            <Modal open={!!confirmAction} onClose={() => setConfirmAction(null)} title={t('confirm_action')} width={380}>
                <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: 14, color: C.text, marginBottom: 20 }}>
                        {t('confirm_action_message', { action: confirmAction.label })}
                    </p>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                        <button onClick={() => setConfirmAction(null)} style={{
                            padding: '8px 20px', borderRadius: 8, border: `1px solid ${C.border}`, cursor: 'pointer',
                            background: C.white, color: C.muted, fontSize: 13, fontWeight: 600, fontFamily: FONT,
                        }}>
                            {t('cancel')}
                        </button>
                        <button onClick={executeConfirmAction} style={{
                            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                            background: confirmAction.type.includes('cancel') ? C.red : C.amber,
                            color: C.white, fontSize: 13, fontWeight: 600, fontFamily: FONT,
                        }}>
                            {t('confirm')}
                        </button>
                    </div>
                </div>
            </Modal>
        );
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════════
    const calendarIcon = (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
    );

    const clockIcon = (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
    );

    return (
        <PortalLayout>
            <ToastContainer toasts={toasts} />
            <ConfirmModal />
            {showNewReservationModal && <NewReservationModal />}
            {showAddWaitlistModal && <AddWaitlistModal />}

            {/* Page header */}
            <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text, fontFamily: FONT }}>{t('reservations')}</h2>
                    <InfoTooltip text={t('reservations_page_info')} />
                </div>
                <p style={{ margin: 0, fontSize: 13, color: C.muted }}>{t('reservations_page_subtitle')}</p>
            </div>

            {/* Tab bar */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 20, background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                {[
                    { id: 'reservations', label: t('reservations'), icon: calendarIcon },
                    { id: 'waitlist', label: t('waiting_list'), icon: clockIcon },
                ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                        flex: 1, padding: '10px 16px', border: 'none', cursor: 'pointer',
                        background: activeTab === tab.id ? C.greenLight : C.white,
                        color: activeTab === tab.id ? C.green : C.muted,
                        fontWeight: activeTab === tab.id ? 700 : 500,
                        fontSize: 13, fontFamily: FONT,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        borderBottom: activeTab === tab.id ? `2px solid ${C.green}` : '2px solid transparent',
                        transition: 'all 0.15s ease',
                    }}>
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* ═══ RESERVATIONS TAB ═══ */}
            {activeTab === 'reservations' && (
                <div>
                    {/* Header row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {/* Date nav */}
                            <button onClick={() => shiftDate(-1)} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: C.muted }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
                            </button>
                            <span style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: FONT, minWidth: 160, textAlign: 'center' }}>
                                {formatDate(selectedDate)}
                            </span>
                            <button onClick={() => shiftDate(1)} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: C.muted }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                            </button>
                            <button onClick={() => setSelectedDate(new Date())} style={{
                                padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`, cursor: 'pointer',
                                background: C.white, color: C.muted, fontSize: 11, fontWeight: 600, fontFamily: FONT,
                            }}>
                                {t('today')}
                            </button>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: isMobile ? 'wrap' : 'nowrap', width: isMobile ? '100%' : 'auto' }}>
                            {/* Filters */}
                            <select value={filterDiningOptionId} onChange={e => setFilterDiningOptionId(e.target.value)} style={{
                                flex: isMobile ? '1 1 45%' : undefined, minWidth: 0,
                                padding: isMobile ? '9px 10px' : '6px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: isMobile ? 13 : 12, fontFamily: FONT, cursor: 'pointer', color: C.text, background: C.white,
                            }}>
                                <option value="">{t('all_dining_options')}</option>
                                {diningOptions.filter(opt => opt.tables_config && typeof opt.tables_config === 'object').map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
                            </select>
                            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{
                                flex: isMobile ? '1 1 45%' : undefined, minWidth: 0,
                                padding: isMobile ? '9px 10px' : '6px 10px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: isMobile ? 13 : 12, fontFamily: FONT, cursor: 'pointer', color: C.text, background: C.white,
                            }}>
                                <option value="">{t('all_statuses')}</option>
                                {Object.keys(RES_STATUS).map(s => <option key={s} value={s}>{t(s)}</option>)}
                            </select>

                            {/* View toggle (desktop only) */}
                            {!isMobile && (
                            <div style={{ display: 'flex', borderRadius: 6, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                                <button onClick={() => setViewMode('list')} style={{
                                    padding: '5px 10px', border: 'none', cursor: 'pointer',
                                    background: viewMode === 'list' ? C.green : C.white,
                                    color: viewMode === 'list' ? C.white : C.muted, fontSize: 11, fontWeight: 600, fontFamily: FONT,
                                }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                                </button>
                                <button onClick={() => setViewMode('calendar')} style={{
                                    padding: '5px 10px', border: 'none', cursor: 'pointer',
                                    background: viewMode === 'calendar' ? C.green : C.white,
                                    color: viewMode === 'calendar' ? C.white : C.muted, fontSize: 11, fontWeight: 600, fontFamily: FONT,
                                }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                </button>
                            </div>
                            )}

                            {/* View floor plan button */}
                            <button onClick={() => openFloorPlan()} style={{
                                flex: isMobile ? '1 1 45%' : undefined, justifyContent: 'center',
                                padding: isMobile ? '11px 14px' : '7px 14px', borderRadius: 8, border: `1.5px solid ${C.border}`,
                                background: C.white, color: C.text, fontSize: isMobile ? 13 : 12, fontWeight: 600,
                                fontFamily: FONT, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                            }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
                                {t('floor_plan')}
                            </button>

                            {/* New reservation button */}
                            <button onClick={() => setShowNewReservationModal(true)} style={{
                                flex: isMobile ? '1 1 45%' : undefined, justifyContent: 'center',
                                padding: isMobile ? '11px 14px' : '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                background: C.green, color: C.white, fontSize: isMobile ? 13 : 12, fontWeight: 700, fontFamily: FONT,
                                display: 'flex', alignItems: 'center', gap: 5,
                            }}>
                                <span style={{ fontSize: 15 }}>+</span> {t('new_reservation')}
                            </button>
                        </div>
                    </div>

                    {/* Mobile hero (list view only) */}
                    {isMobile && viewMode === 'list' && reservations.length > 0 && (() => {
                        const totalCovers = reservations.reduce((s, r) => s + (r.party_size || 0), 0);
                        const confirmed = reservations.filter(r => r.status === 'booked').length;
                        const seated = reservations.filter(r => r.status === 'seated').length;
                        return (
                            <MobileHero
                                label={t('covers') || 'Covers'}
                                value={String(totalCovers)}
                                meta={`${reservations.length} ${reservations.length !== 1 ? t('reservations') : t('reservation') || 'reservation'} · ${formatDate(selectedDate)}`}
                                stats={[
                                    { label: t('booked') || 'Booked', value: String(confirmed) },
                                    { label: t('seated'), value: String(seated), tone: 'green' },
                                ]}
                            />
                        );
                    })()}

                    {/* List view */}
                    {viewMode === 'list' && (
                        reservations.length === 0 ? (
                            <EmptyState
                                icon={calendarIcon}
                                title={t('no_reservations')}
                                subtitle={t('no_reservations_subtitle')}
                                cta={t('create_first_reservation')}
                                onCta={() => setShowNewReservationModal(true)}
                            />
                        ) : isMobile ? (
                            <div>
                                {reservations.map(res => {
                                    const guestName = res.guest?.name || res.guest_name || t('walk_in');
                                    const phone = res.guest?.phone || res.guest_phone;
                                    return (
                                        <MobileCard
                                            key={res.id}
                                            avatar={{ initials: getInitials(guestName), tone: RES_TONE[res.status] || 'gray' }}
                                            title={guestName}
                                            amount={formatTime(res.time)}
                                            status={{ label: t((RES_STATUS[res.status] || {}).label || res.status), tone: RES_TONE[res.status] || 'gray' }}
                                            subtitle={`${res.party_size} ${t('guests') || 'guests'} · ${res.table_number || t('unassigned')}`}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                                {phone && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                                                        <span style={{ color: C.muted, fontWeight: 600 }}>{t('phone')}</span>
                                                        <span style={{ color: C.text, fontWeight: 600 }}>{phone}</span>
                                                    </div>
                                                )}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                                                    <span style={{ color: C.muted, fontWeight: 600 }}>{t('party_size')}</span>
                                                    <span style={{ color: C.text, fontWeight: 600 }}>{res.party_size}</span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                                                    <span style={{ color: C.muted, fontWeight: 600 }}>{t('table')}</span>
                                                    <span style={{ color: res.table_number ? C.text : C.mutedLight, fontWeight: 600 }}>{res.table_number || t('unassigned')}</span>
                                                </div>
                                                {res.notes && (
                                                    <div style={{ fontSize: 13 }}>
                                                        <div style={{ color: C.muted, fontWeight: 600, marginBottom: 2 }}>{t('notes')}</div>
                                                        <div style={{ color: C.text }}>{res.notes}</div>
                                                    </div>
                                                )}
                                                {res.status === 'booked' && (
                                                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                                        <button onClick={() => handleSeatReservation(res)} style={{
                                                            flex: 1, padding: '11px 12px', borderRadius: 8, border: 'none',
                                                            background: C.greenLight, color: C.green, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                                        }}>{t('seat')}</button>
                                                        <button onClick={() => setConfirmAction({ type: 'cancel_reservation', id: res.id, label: t('cancel_reservation') })} style={{
                                                            flex: 1, padding: '11px 12px', borderRadius: 8, border: 'none',
                                                            background: C.redLight, color: C.red, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                                        }}>{t('cancel')}</button>
                                                        <button onClick={() => setConfirmAction({ type: 'noshow_reservation', id: res.id, label: t('mark_no_show') })} style={{
                                                            flex: 1, padding: '11px 12px', borderRadius: 8, border: 'none',
                                                            background: C.amberLight, color: C.amber, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                                        }}>{t('no_show')}</button>
                                                    </div>
                                                )}
                                            </div>
                                        </MobileCard>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                                <div className="portal-table-wrap">
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc', borderBottom: `1px solid ${C.border}` }}>
                                            {[t('time'), t('guest'), t('party_size'), t('table'), t('status'), t('actions')].map((h, i) => (
                                                <th key={i} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, textAlign: 'left' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reservations.map(res => (
                                            <tr key={res.id} style={{ borderBottom: `1px solid ${C.border}` }}
                                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                                onMouseLeave={e => e.currentTarget.style.background = C.white}
                                            >
                                                <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600, color: C.text }}>{formatTime(res.time)}</td>
                                                <td style={{ padding: '10px 14px', fontSize: 13, color: C.text }}>
                                                    <div style={{ fontWeight: 600 }}>{res.guest?.name || res.guest_name || t('walk_in')}</div>
                                                    {(res.guest?.phone || res.guest_phone) && <div style={{ fontSize: 11, color: C.muted }}>{res.guest?.phone || res.guest_phone}</div>}
                                                </td>
                                                <td style={{ padding: '10px 14px', fontSize: 13, color: C.text }}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                                                        {res.party_size}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '10px 14px', fontSize: 13, color: res.table_number ? C.text : C.mutedLight }}>
                                                    {res.table_number || t('unassigned')}
                                                </td>
                                                <td style={{ padding: '10px 14px' }}>
                                                    <StatusBadge status={res.status} map={RES_STATUS} t={t} />
                                                </td>
                                                <td style={{ padding: '10px 14px' }}>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        {res.status === 'booked' && (
                                                            <>
                                                                <ActionBtn label={t('seat')} onClick={() => handleSeatReservation(res)} />
                                                                <ActionBtn label={t('cancel')} onClick={() => setConfirmAction({ type: 'cancel_reservation', id: res.id, label: t('cancel_reservation') })} color={C.red} bg={C.redLight} small />
                                                                <ActionBtn label={t('no_show')} onClick={() => setConfirmAction({ type: 'noshow_reservation', id: res.id, label: t('mark_no_show') })} color={C.amber} bg={C.amberLight} small />
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                </div>
                            </div>
                        )
                    )}

                    {/* Calendar view */}
                    {viewMode === 'calendar' && (
                        <div style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                            {reservations.length === 0 ? (
                                <EmptyState
                                    icon={calendarIcon}
                                    title={t('no_reservations')}
                                    subtitle={t('no_reservations_subtitle')}
                                    cta={t('create_first_reservation')}
                                    onCta={() => setShowNewReservationModal(true)}
                                />
                            ) : (
                                <div style={{ position: 'relative', minHeight: calendarHours.length * 64 }}>
                                    {calendarHours.map(hour => (
                                        <div key={hour} style={{
                                            height: 64, borderBottom: `1px solid ${C.border}`,
                                            display: 'flex', alignItems: 'flex-start', padding: '4px 0',
                                        }}>
                                            <div style={{ width: 60, fontSize: 11, fontWeight: 600, color: C.muted, textAlign: 'right', paddingRight: 10, paddingTop: 2, flexShrink: 0 }}>
                                                {formatTime(`${hour}:00`)}
                                            </div>
                                            <div style={{ flex: 1, position: 'relative', borderLeft: `1px solid ${C.border}`, minHeight: 56, paddingLeft: 8 }}>
                                                {reservations
                                                    .filter(r => {
                                                        const h = parseInt((r.time || '').split(':')[0], 10);
                                                        return h === hour;
                                                    })
                                                    .map(res => {
                                                        const cfg = RES_STATUS[res.status] || RES_STATUS.booked;
                                                        return (
                                                            <div key={res.id} style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                                                background: cfg.bg, border: `1px solid ${cfg.color}20`,
                                                                borderLeft: `3px solid ${cfg.color}`,
                                                                borderRadius: 6, padding: '4px 10px', margin: '2px 4px 2px 0',
                                                                fontSize: 12, fontWeight: 600, color: cfg.color,
                                                                cursor: res.status === 'booked' ? 'pointer' : 'default',
                                                            }} onClick={() => res.status === 'booked' && handleSeatReservation(res)}>
                                                                <span style={{ color: C.text, fontWeight: 600 }}>{formatTime(res.time)}</span>
                                                                <span>{res.guest?.name || res.guest_name || t('walk_in')}</span>
                                                                <span style={{ fontSize: 11, color: C.muted }}>({res.party_size})</span>
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ═══ WAITING LIST TAB ═══ */}
            {activeTab === 'waitlist' && (
                <div>
                    {/* Header row */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <select value={filterDiningOptionId} onChange={e => setFilterDiningOptionId(e.target.value)} style={{
                                padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, fontFamily: FONT, cursor: 'pointer', color: C.text, background: C.white,
                            }}>
                                <option value="">{t('all_dining_options')}</option>
                                {diningOptions.filter(opt => opt.tables_config && typeof opt.tables_config === 'object').map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
                            </select>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {/* View floor plan button */}
                            <button onClick={() => openFloorPlan()} style={{
                                padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${C.border}`,
                                background: C.white, color: C.text, fontSize: 12, fontWeight: 600,
                                fontFamily: FONT, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                            }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
                                {t('floor_plan')}
                            </button>

                            <button onClick={() => setShowAddWaitlistModal(true)} style={{
                                padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                background: C.green, color: C.white, fontSize: 12, fontWeight: 700, fontFamily: FONT,
                                display: 'flex', alignItems: 'center', gap: 5,
                            }}>
                                <span style={{ fontSize: 15 }}>+</span> {t('add_to_waitlist')}
                            </button>
                        </div>
                    </div>

                    {/* Mobile hero (waitlist) */}
                    {isMobile && waitlistEntries.length > 0 && (
                        <MobileHero
                            label={t('waiting_list')}
                            value={String(waitlistEntries.filter(e => e.status === 'waiting' || e.status === 'notified').length)}
                            meta={`${waitlistEntries.reduce((s, e) => s + (e.party_size || 0), 0)} ${t('guests') || 'guests'} ${t('waiting') || 'waiting'}`}
                        />
                    )}

                    {/* Waitlist entries */}
                    {waitlistEntries.length === 0 ? (
                        <EmptyState
                            icon={clockIcon}
                            title={t('no_waitlist_entries')}
                            subtitle={t('no_waitlist_subtitle')}
                            cta={t('add_first_guest')}
                            onCta={() => setShowAddWaitlistModal(true)}
                        />
                    ) : isMobile ? (
                        <div>
                            {waitlistEntries.map((entry, idx) => {
                                const guestName = entry.guest?.name || entry.guest_name || t('walk_in');
                                const canSeat = entry.status === 'waiting' || entry.status === 'notified';
                                return (
                                    <MobileCard
                                        key={entry.id}
                                        avatar={{ initials: String(idx + 1), tone: WL_TONE[entry.status] || 'gray' }}
                                        title={guestName}
                                        amount={`${entry.party_size}p`}
                                        status={{ label: t((WL_STATUS[entry.status] || {}).label || entry.status), tone: WL_TONE[entry.status] || 'gray' }}
                                        subtitle={`${t('waiting_for')} ${timeAgo(entry.joined_at || entry.created_at, t)}`}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                                                <span style={{ color: C.muted, fontWeight: 600 }}>{t('party_size')}</span>
                                                <span style={{ color: C.text, fontWeight: 600 }}>{entry.party_size}</span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                                                <span style={{ color: C.muted, fontWeight: 600 }}>{t('waiting_for')}</span>
                                                <span style={{ color: C.text, fontWeight: 600 }}>{timeAgo(entry.joined_at || entry.created_at, t)}</span>
                                            </div>
                                            {entry.expected_wait_minutes && (
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
                                                    <span style={{ color: C.muted, fontWeight: 600 }}>{t('expected_wait')}</span>
                                                    <span style={{ color: C.text, fontWeight: 600 }}>{entry.expected_wait_minutes}{t('min')}</span>
                                                </div>
                                            )}
                                            {canSeat && (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                                                    {entry.status === 'waiting' && (
                                                        <button onClick={() => handleNotifyWaitlist(entry.id)} style={{
                                                            flex: 1, minWidth: 70, padding: '11px 12px', borderRadius: 8, border: 'none',
                                                            background: C.amberLight, color: C.amber, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                                        }}>{t('notify')}</button>
                                                    )}
                                                    <button onClick={() => handleSeatWaitlistEntry(entry)} style={{
                                                        flex: 1, minWidth: 70, padding: '11px 12px', borderRadius: 8, border: 'none',
                                                        background: C.greenLight, color: C.green, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                                    }}>{t('seat')}</button>
                                                    <button onClick={() => setConfirmAction({ type: 'leave_waitlist', id: entry.id, label: t('mark_left') })} style={{
                                                        flex: 1, minWidth: 70, padding: '11px 12px', borderRadius: 8, border: 'none',
                                                        background: '#f1f5f9', color: C.muted, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                                    }}>{t('left')}</button>
                                                    <button onClick={() => setConfirmAction({ type: 'noshow_waitlist', id: entry.id, label: t('mark_no_show') })} style={{
                                                        flex: 1, minWidth: 70, padding: '11px 12px', borderRadius: 8, border: 'none',
                                                        background: C.amberLight, color: C.amber, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: FONT,
                                                    }}>{t('no_show')}</button>
                                                </div>
                                            )}
                                        </div>
                                    </MobileCard>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {waitlistEntries.map((entry, idx) => {
                                const wlCfg = WL_STATUS[entry.status] || WL_STATUS.waiting;
                                return (
                                    <div key={entry.id} style={{
                                        background: C.white, borderRadius: 10, border: `1px solid ${C.border}`,
                                        padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 14,
                                    }}
                                        onMouseEnter={e => e.currentTarget.style.borderColor = '#cbd5e1'}
                                        onMouseLeave={e => e.currentTarget.style.borderColor = C.border}
                                    >
                                        {/* Position badge */}
                                        <div style={{
                                            width: 32, height: 32, borderRadius: '50%', background: C.greenLight,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 13, fontWeight: 800, color: C.green, flexShrink: 0,
                                        }}>
                                            {idx + 1}
                                        </div>

                                        {/* Guest info */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{entry.guest?.name || entry.guest_name || t('walk_in')}</div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 2 }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, color: C.muted }}>
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                                                    {entry.party_size}
                                                </span>
                                                <span style={{ fontSize: 12, color: C.muted }}>
                                                    {t('waiting_for')} {timeAgo(entry.joined_at || entry.created_at, t)}
                                                </span>
                                                {entry.expected_wait_minutes && (
                                                    <span style={{ fontSize: 11, color: C.mutedLight }}>
                                                        ({t('est')} {entry.expected_wait_minutes}{t('min')})
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <StatusBadge status={entry.status} map={WL_STATUS} t={t} />

                                        {/* Actions */}
                                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                            {entry.status === 'waiting' && (
                                                <>
                                                    <ActionBtn label={t('notify')} onClick={() => handleNotifyWaitlist(entry.id)} color={C.amber} bg={C.amberLight} />
                                                    <ActionBtn label={t('seat')} onClick={() => handleSeatWaitlistEntry(entry)} />
                                                    <ActionBtn label={t('left')} onClick={() => setConfirmAction({ type: 'leave_waitlist', id: entry.id, label: t('mark_left') })} color={C.muted} bg="#f1f5f9" small />
                                                    <ActionBtn label={t('no_show')} onClick={() => setConfirmAction({ type: 'noshow_waitlist', id: entry.id, label: t('mark_no_show') })} color={C.amber} bg={C.amberLight} small />
                                                </>
                                            )}
                                            {entry.status === 'notified' && (
                                                <>
                                                    <ActionBtn label={t('seat')} onClick={() => handleSeatWaitlistEntry(entry)} />
                                                    <ActionBtn label={t('left')} onClick={() => setConfirmAction({ type: 'leave_waitlist', id: entry.id, label: t('mark_left') })} color={C.muted} bg="#f1f5f9" small />
                                                    <ActionBtn label={t('no_show')} onClick={() => setConfirmAction({ type: 'noshow_waitlist', id: entry.id, label: t('mark_no_show') })} color={C.amber} bg={C.amberLight} small />
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ═══ FLOOR PLAN MODAL ═══ */}
            {showFloorPlan && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {/* Backdrop */}
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} onClick={() => { if (!seatMode) { setShowFloorPlan(false); } }} />
                    {/* Modal */}
                    <div className="portal-modal-card" style={{ position: 'relative', background: C.white, borderRadius: 16, width: '90%', maxWidth: 900, maxHeight: '85vh', overflow: 'auto', padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, fontFamily: FONT, color: C.text }}>{t('floor_plan')}</h2>
                            <button onClick={() => { setSeatMode(null); setShowFloorPlan(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>

                        {/* Seat mode banner */}
                        {seatMode && (
                            <div style={{ background: C.greenLight, border: `1px solid ${C.greenBorder}`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: C.greenDark, fontFamily: FONT }}>{t('select_table_to_seat')}</span>
                                <button onClick={() => setSeatMode(null)} style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: C.white, color: C.red, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT }}>{t('cancel')}</button>
                            </div>
                        )}

                        {/* Dining option selector */}
                        <select value={selectedDiningOptionId || ''} onChange={e => setSelectedDiningOptionId(parseInt(e.target.value))} style={{
                            padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`,
                            fontSize: 12, fontFamily: FONT, cursor: 'pointer', marginBottom: 16, color: C.text, background: C.white,
                        }}>
                            {diningOptions.filter(opt => opt.tables_config && typeof opt.tables_config === 'object' && opt.tables_config !== null).map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
                        </select>

                        {/* Floor plan */}
                        <VisualTableSelector
                            selectedDiningOption={selectedDiningOptionName}
                            selectedTable={null}
                            onTableSelect={seatMode ? handleTableSelect : () => {}}
                            diningOptions={diningOptionsMap}
                            occupations={activeOccupations}
                            isVisible={true}
                            scaleToFit={isMobile}
                        />

                        {/* Legend */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 11, color: C.muted, marginTop: 16 }}>
                            {[
                                { color: '#10B981', label: t('available') },
                                { color: '#3B82F6', label: t('seated') },
                                { color: '#EF4444', label: t('ordered') },
                                { color: '#d97706', label: t('awaiting_bill') },
                                { color: '#94a3b8', label: t('paid') },
                            ].map(item => (
                                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: item.color }} />
                                    <span>{item.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </PortalLayout>
    );
};

export default PortalReservationsPage;
